import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Slider,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocalFireDepartment as FireIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { 
  placePromotionBid, 
  getPromotionStatus, 
  cancelPromotionBid,
  PromotionBidRequest,
  TopBid,
  PromotionBid
} from '../lib/api/promotionBids';

interface PromotionModalProps {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemType: 'SERVICE' | 'PROJECT' | 'FREELANCER_PROFILE';
  itemTitle: string;
  onPromotionSuccess?: () => void;
}

const PromotionModal: React.FC<PromotionModalProps> = ({
  open,
  onClose,
  itemId,
  itemType,
  itemTitle,
  onPromotionSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Promotion data
  const [creditsBid, setCreditsBid] = useState(10);
  const [duration, setDuration] = useState(1); // Duration in days
  const [userCredits, setUserCredits] = useState(0);
  const [topBid, setTopBid] = useState<TopBid | null>(null);
  const [userBid, setUserBid] = useState<PromotionBid | null>(null);
  const [canBid, setCanBid] = useState(false);
  const [isCurrentlyPromoted, setIsCurrentlyPromoted] = useState(false);

  // Load promotion status when modal opens
  useEffect(() => {
    if (open) {
      loadPromotionStatus();
    }
  }, [open, itemId, itemType]);

  const loadPromotionStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const status = await getPromotionStatus(itemId, itemType);
      setUserCredits(status.userCredits);
      setTopBid(status.topBid);
      setUserBid(status.userBid);
      setCanBid(status.canBid);
      setIsCurrentlyPromoted(status.isPromoted);
      
      // Set minimum bid to be higher than current top bid
      if (status.topBid) {
        setCreditsBid(Math.max(status.topBid.credits_bid + 1, 10));
      }
    } catch (err: any) {
      setError(err.message || 'Грешка при зареждане на данните');
    } finally {
      setLoading(false);
    }
  };

  const handleBidChange = (_: Event, newValue: number | number[]) => {
    setCreditsBid(newValue as number);
  };

  const handleDurationChange = (_: Event, newValue: number | number[]) => {
    setDuration(newValue as number);
  };

  const handleSubmit = async () => {
    if (!canBid) {
      setError('Нямате права за промотиране на този елемент');
      return;
    }

    const totalCost = getTotalCost();
    if (totalCost > userCredits) {
      setError(`Нямате достатъчно кредити. Нужни: ${totalCost}, налични: ${userCredits}`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const bidRequest: PromotionBidRequest = {
        item_id: itemId,
        item_type: itemType,
        credits_bid: totalCost, // Use total cost instead of per-day bid
        duration_hours: duration * 24 // Convert days to hours for database
      };

      await placePromotionBid(bidRequest);
      
      setSuccess(true);
      onPromotionSuccess?.();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
      
    } catch (err: any) {
      setError(err.message || 'Грешка при създаване на промоцията');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelPromotion = async () => {
    if (!userBid) return;

    setSubmitting(true);
    setError(null);

    try {
      await cancelPromotionBid(userBid.id);
      
      setSuccess(true);
      onPromotionSuccess?.();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
      
    } catch (err: any) {
      setError(err.message || 'Грешка при отмяна на промоцията');
    } finally {
      setSubmitting(false);
    }
  };

  const getItemTypeLabel = () => {
    switch (itemType) {
      case 'SERVICE': return 'услуга';
      case 'PROJECT': return 'проект';
      case 'FREELANCER_PROFILE': return 'профил';
      default: return 'елемент';
    }
  };

  const getDurationLabel = (days: number) => {
    if (days === 1) return '1 ден';
    return `${days} дни`;
  };

  const getTotalCost = () => {
    return creditsBid * duration;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
          border: '1px solid rgba(40, 160, 240, 0.3)',
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        color: '#ffffff',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FireIcon sx={{ color: '#ff5722', fontSize: '1.5rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Промотирай {getItemTypeLabel()}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#a0a0a0' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ color: '#ffffff' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#28A0F0' }} />
          </Box>
        ) : success ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <FireIcon sx={{ fontSize: 60, color: '#ff5722', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
              {isCurrentlyPromoted ? 'Промоцията е отменена!' : 'Промоцията е създадена успешно!'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
              {isCurrentlyPromoted 
                ? `Вашата ${getItemTypeLabel()} вече не е промотирана.`
                : `Вашата ${getItemTypeLabel()} ще бъде показана на топ позиция за ${getDurationLabel(duration)}.`
              }
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 3, color: '#d0d0d0' }}>
              Промотирайте "{itemTitle}" с наддаване на кредити. Изберете колко кредита да давате на ден 
              и за колко дни. Общата цена ще бъде: <strong>{getTotalCost()} кредита</strong>.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {!canBid && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Нямате права за промотиране на този елемент или нямате достатъчно кредити.
              </Alert>
            )}

            {/* Current Promotion Status */}
            {isCurrentlyPromoted && userBid && (
              <Box sx={{ 
                mb: 4, 
                p: 3, 
                backgroundColor: 'rgba(255, 87, 34, 0.1)', 
                borderRadius: 2,
                border: '1px solid rgba(255, 87, 34, 0.3)'
              }}>
                <Typography variant="h6" sx={{ color: '#ff5722', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FireIcon sx={{ fontSize: '1.2rem' }} />
                  Текущо промотиране
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                    Общо платени кредити:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    {userBid.credits_bid} кредита
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                    Изтича на:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    {new Date(userBid.expires_at).toLocaleString('bg-BG')}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                    Статус:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ff5722', fontWeight: 600 }}>
                    АКТИВНО
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Current Status */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                Текущо състояние
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip
                  icon={<MoneyIcon />}
                  label={`Ваши кредити: ${userCredits}`}
                  sx={{
                    backgroundColor: 'rgba(40, 160, 240, 0.2)',
                    color: '#28A0F0',
                    border: '1px solid #28A0F0',
                  }}
                />
                {topBid && (
                  <Chip
                    icon={<FireIcon />}
                    label={`Текущо лидер: ${topBid.credits_bid} кредита`}
                    sx={{
                      backgroundColor: 'rgba(255, 87, 34, 0.2)',
                      color: '#ff5722',
                      border: '1px solid #ff5722',
                    }}
                  />
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* Bid Amount */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                Кредити на ден
              </Typography>
              
              <Box sx={{ px: 2 }}>
                <Slider
                  value={creditsBid}
                  onChange={handleBidChange}
                  min={1}
                  max={Math.min(userCredits, 1000)}
                  step={1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value} кредита/ден`}
                  sx={{
                    color: '#28A0F0',
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#28A0F0',
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#28A0F0',
                    },
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  Минимум: 1 кредит/ден
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  Максимум: {Math.min(userCredits, 1000)} кредита/ден
                </Typography>
              </Box>
            </Box>

            {/* Duration */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                Продължителност на промоцията
              </Typography>
              
              <Box sx={{ px: 2 }}>
                <Slider
                  value={duration}
                  onChange={handleDurationChange}
                  min={1}
                  max={30} // 30 days max
                  step={1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => getDurationLabel(value)}
                  sx={{
                    color: '#ff5722',
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#ff5722',
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#ff5722',
                    },
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  Минимум: 1 ден
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  Максимум: 30 дни
                </Typography>
              </Box>
            </Box>

            {/* Summary */}
            <Box sx={{ 
              p: 3, 
              backgroundColor: 'rgba(40, 160, 240, 0.1)', 
              borderRadius: 2,
              border: '1px solid rgba(40, 160, 240, 0.3)'
            }}>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                Резюме на промоцията
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  Кредити на ден:
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                  {creditsBid} кредита/ден
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                  Продължителност:
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                  {getDurationLabel(duration)}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                  Общо:
                </Typography>
                <Typography variant="body1" sx={{ color: '#28A0F0', fontWeight: 700 }}>
                  {getTotalCost()} кредита
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      {!loading && !success && (
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={onClose}
            sx={{
              color: '#a0a0a0',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            Отказ
          </Button>
          
          {isCurrentlyPromoted && userBid ? (
            <Button
              onClick={handleCancelPromotion}
              disabled={submitting}
              variant="contained"
              startIcon={submitting ? <CircularProgress size={16} /> : <CloseIcon />}
              sx={{
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                color: '#ffffff',
                px: 3,
                py: 1,
                '&:hover': {
                  background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              {submitting ? 'Отмяна...' : 'Отмени промотирането'}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canBid || submitting || getTotalCost() > userCredits}
              variant="contained"
              startIcon={submitting ? <CircularProgress size={16} /> : <FireIcon />}
              sx={{
                background: 'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)',
                color: '#ffffff',
                px: 3,
                py: 1,
                '&:hover': {
                  background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              {submitting ? 'Създаване...' : `Промотирай за ${getTotalCost()} кредита`}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PromotionModal;
